<template>
    <div class="echart-chart-block">
        <div :id="chartID" class="chart-tall"></div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import echarts from 'echarts'
import ecTools from '@/echarts/echarts-tools'

export default {
    props:
    {
        chartID: {
            type: String,
            required: true,
        },
        stressCode: {
            type: String,
            required: true,
        },
        data: {
            type: Array,
            default: function () { return [] }
        },
        time: {
            type: Array,
            default: function () { return [] }
        },
        deviceInfo: {
            type: Object,
            default: function () { return {} }
        },
        showToolbox: {
            type: Boolean,
            default: true,
        },
        preSaveName: {
            type: String,
            default: '',
        },
        displayColor: {
            type: String,
            // default: 'red',
        },
        filterTextMax: {
            type: String,
        },
        filterTextMin: {
            type: String,
        },
    },
    data () {
        return {
            theChart: undefined,
            lastValue: undefined,

            calendar_range: '',
            calendar_day: [],
        }
    },
    mounted () {
        this.theChart = echarts.init(document.getElementById( this.chartID ));
        this.drawChartPrepare();
    },
    computed: {
        ...mapState({
            'language': state => state.user.language,
            'screenWidth': state => state.system.screenWidth,
            'contentWidth': state => state.system.contentWidth,
        }),

        sensorStyle ()
        {
            let _setting = ecTools.getSensorSettings( this.sensorCode )

            return {
                color: ecTools.chart_HeatMap_Color[ _setting.color ][2]
            }
        },

        getEchartsLanguage ()
        {
            switch (this.language)
            {
                case 'zh-Hant-TW':
                case 'zh-Hant':
                case 'zh-Hans': return 'cn';
                default: return 'en';
            }
        },
    },
    watch: {
        // 偵測到 [ 語言 ] 變動就更新圖表
        language ()
        {
            this.drawChartPrepare();
        },

        // 偵測到 [ 時間 ] 變動就更新圖表
        time ()
        {
            this.drawChartPrepare();
        },

        // 偵測到 [ 資料 ] 變動就更新圖表
        data ()
        {
            this.drawChartPrepare();
        },


        // 偵測到 [ 視窗寬度 ] 變動就更新圖表
        screenWidth (val, oldVal)
        {
            if ( val != oldVal && this.theChart != undefined )
            {
                this.theChart.resize()
            }
        },
        // 偵測到 [ 版面寬度 ] 變動就更新圖表
        contentWidth (val, oldVal)
        {
            if ( val != oldVal && this.theChart != undefined )
            {
                this.theChart.resize()
            }
        },
    },
    methods: {

        drawChartPrepare ()
        {
            // if (this.data && this.data.length > 0) {
                this.drawChart( this.theChart,
                                this.stressCode,
                                this.data,
                                this.time,
                                this.showToolbox );
            // }
        },
        
        drawChart ( chart,
                    _stressCode,
                    _data,
                    _time,
                    _showToolbox )
        {
            // let hours = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10','11','12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24'];
            let weekdays = [
                this.$t('date.Sun'),
                this.$t('date.Mon'),
                this.$t('date.Tue'),
                this.$t('date.Wed'),
                this.$t('date.Thu'),
                this.$t('date.Fri'),
                this.$t('date.Sat'),
            ];
            let _stressName = this.$t('stress.' + _stressCode);
            let _color = ( this.displayColor != undefined ? ecTools.chart_HeatMap_Color[ this.displayColor ] : ecTools.getStressHeatMap( _stressCode ) );
            let _precision = 0;

            let _data_Line = [];
            let bounding = [ 0, 100 ];
            let bounding_limit = [ undefined, undefined ];


            _data_Line = this.getArrangedData();
            for (let i=0; i<_data_Line.length; i++)
            {
                _data_Line[i][1] = ecTools.checkDataBounding(_data_Line[i][1], bounding, bounding_limit, _precision);
            }
            
            // 檢查視窗大小，顯示/隱藏項目
            let _chartWidth = chart.getWidth();
            let _this = this

            let option = {
                toolbox: ecTools.createToolbox( this.chartID, this.preSaveName + ' - ' + _stressName + ' - HeatMap Chart', _showToolbox ),
                tooltip:
                {
                    formatter: function (params) {
                        // console.log(params);
                        let _tooltip = params.data[0] + '<br>'
                        _tooltip += '<i class="fa fa-circle" style="color:'+ params.color +';"></i> ' + _this.$t('menu.data_count') + ': ' + params.data[1];

                        for (let _deviceHashid in _this.calendar_day[ params.dataIndex ])
                        {
                            let _deviceInfo = _this.deviceInfo[ _deviceHashid ]
                            
                            _tooltip += `<br>
                                        <i class=" ${ _this.$tools.style.classIcon(_deviceInfo.type, 'block-text') }"></i>
                                        ${ _deviceInfo.nickname } :
                                        ${ _this.calendar_day[ params.dataIndex ][ _deviceHashid ] }`
                                        // ${ _this.$t('n.' + _deviceInfo.type) }
                        }
                        return _tooltip;
                    },
                },
                grid:
                [
                    {
                        id: 0,
                        x: '3%',
                        top: '8%',
                        bottom: '16%',
                        width: '95%',
                    },
                ],
                xAxis:
                [
                    {
                        gridIndex: 0,
                        type: 'category',
                        nameGap: 5,
                        splitArea: { show: false },
                        axisLine: { show: false },
                        axisTick: { show: false }
                    },
                ],
                yAxis:
                [
                    {
                        gridIndex: 0,
                        type: 'category',
                        splitArea: { show: false },
                        axisLine: { show: false },
                        axisTick: { show: false }
                    },
                ],
                
                visualMap:
                [
                    {
                        gridIndex: 0,
                        
                        xAxisIndex: 0,
                        yAxisIndex: 0,

                        min: 0,
                        max: bounding[1],
                        calculable: true,
                        orient: 'horizontal',
                        left: '22%',
                        bottom: 0,
                        align: 'bottom',
                        text: [
                            ( this.filterTextMax != undefined ? this.filterTextMax : this.$t('menu.max') ),
                            ( this.filterTextMin != undefined ? this.filterTextMin : this.$t('menu.min') )
                        ],
                        textGap: 5,
                        
                        inRange:    { color: _color }, // 超過的數值就會是最大值的顏色
                        outOfRange: { color: '#e0ebeb' },
                    },
                ],
                calendar: [
                    {
                        orient: 'vertical',
                        top: 70,
                        bottom: 40,
                        right: 55,
                        yearLabel: {
                            // show: false,
                            position: 'top',
                            margin: 35,
                            color: '#174232',
                            fontWeight: 600,
                        },
                        monthLabel: {
                            align: 'center',
                            verticalAlign: 'middle',
                            margin: 10,
                            nameMap: this.getEchartsLanguage,
                        },
                        dayLabel: {
                            margin: 10,
                            firstDay: 0,
                            nameMap: weekdays,
                        },
                        cellSize: 40,
                        range: this.calendar_range, //'2018-01',
                    },
                ],
                series: [
                    {
                        type: 'effectScatter',
                        coordinateSystem: 'calendar',
                        calendarIndex: 0,
                        symbolSize: function (val) {
                            let _size = val[1] / 10

                            if ( _size > 18 )      return 18
                            else if ( _size < 1 )   return 0
                            else if ( _size < 5 )   return 5
                            return _size;
                        },
                        data: _data_Line, //this.getVirtulData(2018)
                    },
                ],
                animation: true,
            };
            chart.setOption(option);
        },

        getArrangedData ()
        {
            let _result = []
            this.calendar_day = []
            this.calendar_range = ''

            if ( this.data.length == this.time.length && this.time.length > 0 )
            {
                this.calendar_range = this.$tools.date.dateFormat( this.time[0], 'YYYY-MM' )
                // console.log( this.calendar_range )

                for (let i=0; i< this.time.length; i++)
                {
                    _result.push( [ this.time[i], this.data[i].length ] )

                    let _count_by_device = {}
                    this.data[i].forEach( __alert => {
                        if ( _count_by_device[ __alert.hashid ] == undefined ) _count_by_device[ __alert.hashid ] = 1
                        else _count_by_device[ __alert.hashid ] += 1
                    })
                    // console.log( _count_by_device )
                    this.calendar_day.push( _count_by_device )
                }
            }

            return _result
        },

        // getVirtulData (year)
        // {
        //     year = year || '2017';
        //     let date = + echarts.number.parseDate(year + '-01-01');
        //     let end = + echarts.number.parseDate((+year + 1) + '-01-01');
        //     let dayTime = 3600 * 24 * 1000;
        //     let data = [];
        //     for (let time = date; time < end; time += dayTime) {
        //         data.push([
        //             echarts.format.formatTime('yyyy-MM-dd', time),
        //             Math.floor(Math.random() * 1000)
        //         ]);
        //     }
        //     console.log( data[data.length - 1] );
        //     return data;
        // },

    }
    
}
</script>

<style lang="scss" scoped>

.chart-tall
{
    width: 420px;
    padding: 10px;
}

</style>
